import type {
  StorageSiteMapAreaFeatureType,
  StorageSiteMapFeature,
  StorageSiteMapFeatureCollection,
  StorageSiteMapFeatureProperties,
  StorageSiteMapFeatureType,
  StorageSiteMapGeometry,
  StorageSiteMapLineFeatureType,
  StorageSiteMapStorageAreaFeatureType,
  StorageSiteMapStorageAreaProperties,
  StorageSiteMapSymbolFeatureType,
} from '../types/storage-site-map.ts';

export const STORAGE_SITE_MAP_MAX_FEATURES = 500;
export const STORAGE_SITE_MAP_MAX_LABEL_LENGTH = 200;
export const STORAGE_SITE_MAP_MAX_COORDINATES_PER_SHAPE = 2_000;

export const STORAGE_SITE_MAP_STORAGE_AREA_FEATURE_TYPES = [
  'central-accumulation-area',
  'satellite-accumulation-area',
  'permitted-storage-area',
  'other-storage-area',
] as const satisfies readonly StorageSiteMapStorageAreaFeatureType[];

export const STORAGE_SITE_MAP_SYMBOL_FEATURE_TYPES = [
  'entrance',
  'water-supply',
  'notification-system',
] as const satisfies readonly StorageSiteMapSymbolFeatureType[];

export const STORAGE_SITE_MAP_LINE_FEATURE_TYPES = [
  'pathway',
  'evacuation-route',
] as const satisfies readonly StorageSiteMapLineFeatureType[];

export const STORAGE_SITE_MAP_AREA_FEATURE_TYPES = [
  'building-outline',
  'room',
] as const satisfies readonly StorageSiteMapAreaFeatureType[];

export function storageSiteMapIsStorageAreaType(
  featureType: StorageSiteMapFeatureType,
): featureType is StorageSiteMapStorageAreaFeatureType {
  return (STORAGE_SITE_MAP_STORAGE_AREA_FEATURE_TYPES as readonly StorageSiteMapFeatureType[]).includes(featureType);
}

export function storageSiteMapIsStorageArea(
  properties: StorageSiteMapFeatureProperties,
): properties is StorageSiteMapStorageAreaProperties {
  return storageSiteMapIsStorageAreaType(properties.featureType);
}

/**
 * Pairs properties with a drawn geometry, or returns null when that feature type cannot be drawn with that
 * geometry (e.g. a storage-area pin drawn as a polygon).
 */
export function storageSiteMapFeatureCreate(
  properties: StorageSiteMapFeatureProperties,
  geometry: StorageSiteMapGeometry,
): StorageSiteMapFeature | null {
  switch (properties.featureType) {
    case 'central-accumulation-area':
    case 'satellite-accumulation-area':
    case 'permitted-storage-area':
    case 'other-storage-area':
      return geometry.type === 'Point' ? { type: 'Feature', geometry, properties } : null;
    case 'entrance':
    case 'water-supply':
    case 'notification-system':
      return geometry.type === 'Point' ? { type: 'Feature', geometry, properties } : null;
    case 'pathway':
    case 'evacuation-route':
      return geometry.type === 'LineString' ? { type: 'Feature', geometry, properties } : null;
    case 'building-outline':
    case 'room':
      return geometry.type === 'Polygon' ? { type: 'Feature', geometry, properties } : null;
  }
}

/**
 * Checks what the feature types cannot express. Feature type/geometry pairing, storage-location links,
 * and string/coordinate bounds are all enforced by the types and the transport schema.
 */
export function storageSiteMapValidationErrors(collection: StorageSiteMapFeatureCollection): string[] {
  const errors: string[] = [];
  for (const [index, feature] of collection.features.entries()) {
    if (feature.geometry.type !== 'Polygon') continue;
    for (const ring of feature.geometry.coordinates) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (!first || !last || first[0] !== last[0] || first[1] !== last[1]) {
        errors.push(`features[${index}] polygon ring must be closed.`);
      }
    }
  }
  return errors;
}

export function storageSiteMapAssertValid(collection: StorageSiteMapFeatureCollection) {
  const errors = storageSiteMapValidationErrors(collection);
  if (errors.length > 0) throw new Error(`Invalid site map: ${errors.slice(0, 5).join(' ')}`);
}
